import { PageHeader } from '@/shared/components/ui/PageHeader';
// ─────────────────────────────────────────────────────────────────────────────
// BIZPLUS PARITY — E-way Bill  (Phase 5.4 D)
//
// Two things this screen must never do, because both end with a lorry detained:
//   · imply a bill exists when the portal never issued one — the EWB number is
//     shown only when it came back from NIC
//   · let an expiring bill go unnoticed — anything within a day is surfaced at
//     the top, ahead of the list
//
// It also states plainly when the NIC integration is not configured, rather
// than failing at the moment somebody presses Generate with goods on the bay.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { toast } from 'react-hot-toast';
import { Truck, AlertTriangle, Clock, Ban, CheckCircle2, Plug } from 'lucide-react';

const money = (v: unknown) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');

export function EwayBillPage() {
  const qc = useQueryClient();
  const [showDraft, setShowDraft] = useState(false);

  const { data: status } = useQuery({
    queryKey: ['ewb', 'status'],
    queryFn: async () => (await api.get('/api/ewaybill/status')).data,
  });
  const { data } = useQuery({
    queryKey: ['ewb', 'list'],
    queryFn: async () => (await api.get('/api/ewaybill')).data,
  });
  const { data: expiring } = useQuery({
    queryKey: ['ewb', 'expiring'],
    queryFn: async () => (await api.get('/api/ewaybill/expiring?withinDays=1')).data,
    refetchInterval: 300000,
  });

  const generate = useMutation({
    mutationFn: async (id: string) => (await api.post(`/api/ewaybill/${id}/generate`)).data,
    onSuccess: (d: any) => {
      toast.success(`Generated ${d.item?.ewbNo}.`);
      qc.invalidateQueries({ queryKey: ['ewb'] });
    },
    onError: (e: any) => {
      const err = e?.response?.data;
      toast.error(
        `${err?.error || 'Generation failed.'}${err?.nicErrorCode ? ` (NIC ${err.nicErrorCode})` : ''}`,
        { duration: 9000 },
      );
    },
  });

  const cancel = useMutation({
    mutationFn: async (id: string) => (await api.post(`/api/ewaybill/${id}/cancel`, { reasonCode: 4, remark: 'Cancelled from Zynx' })).data,
    onSuccess: () => { toast.success('Cancelled.'); qc.invalidateQueries({ queryKey: ['ewb'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Cancel failed.'),
  });

  const items = data?.items || [];
  const soon = expiring?.items || [];

  return (
    <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
      <PageHeader icon={Truck} eyebrow="Finance" title="E-way Bills" description={<>Generate against the NIC portal, update the vehicle, and track expiry.</>} actions={<><button onClick={() => setShowDraft(!showDraft)}
          className="px-3 py-2 rounded-lg border border-outline text-sm font-semibold">
          {showDraft ? 'Close' : 'New from invoice'}
        </button></>} />

      {/* Said up front. Discovering this when the goods are loaded is the
          failure mode worth designing against. */}
      {status && !status.configured && (
        <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-sm text-amber-900 dark:text-amber-200 flex gap-3">
          <Plug className="w-5 h-5 shrink-0 text-amber-600" />
          <div>
            <p className="font-semibold">The NIC E-way Bill integration is not configured.</p>
            <p className="text-xs mt-1">
              Bills can be drafted and validated here, but none can be generated until the
              credentials are set. Missing: {(status.missing || []).join(', ')}.
            </p>
          </div>
        </div>
      )}
      {status?.configured && (
        <p className="text-xs text-on-surface-variant flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          {status.note}
        </p>
      )}

      {soon.length > 0 && (
        <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800">
          <p className="font-semibold text-sm text-rose-800 dark:text-rose-300 flex items-center gap-2">
            <Clock className="w-4 h-4" /> {soon.length} bill(s) expire within a day
          </p>
          <ul className="mt-2 text-xs space-y-1 text-rose-700 dark:text-rose-300">
            {soon.map((b: any) => (
              <li key={b._id}>
                <span className="font-mono">{b.ewbNo}</span> — {b.docNo} ·{' '}
                {b.daysLeft === 0 ? 'expires today' : `${b.daysLeft} day left`} (valid to {fmt(b.validUpto)})
              </li>
            ))}
          </ul>
        </div>
      )}

      {showDraft && <DraftFromInvoice onDone={() => { setShowDraft(false); qc.invalidateQueries({ queryKey: ['ewb'] }); }} />}

      {items.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-outline rounded-xl text-sm text-on-surface-variant">
          No e-way bills yet.
        </div>
      ) : (
        <div className="rounded-xl border border-outline overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-variant/30 text-on-surface-variant">
              <tr>
                <th className="text-left px-4 py-2">EWB No.</th>
                <th className="text-left px-4 py-2">Document</th>
                <th className="text-left px-4 py-2">To</th>
                <th className="text-right px-4 py-2">Value</th>
                <th className="text-left px-4 py-2">Vehicle</th>
                <th className="text-left px-4 py-2">Valid to</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((b: any) => (
                <tr key={b._id} className="border-t border-outline/50">
                  {/* Blank until NIC issues one — never a placeholder that could
                      be mistaken for a real number on a printed challan. */}
                  <td className="px-4 py-2 font-mono">{b.ewbNo || <span className="text-on-surface-variant">not generated</span>}</td>
                  <td className="px-4 py-2">{b.docNo}<div className="text-on-surface-variant">{fmt(b.docDate)}</div></td>
                  <td className="px-4 py-2">{b.to?.name || '—'}<div className="text-on-surface-variant">{b.to?.place}</div></td>
                  <td className="px-4 py-2 text-right">{money(b.totalInvoiceValue)}</td>
                  <td className="px-4 py-2 font-mono">{b.vehicleNo || '—'}</td>
                  <td className="px-4 py-2">
                    {fmt(b.validUpto)}
                    {b.daysLeft !== null && b.status === 'active' && (
                      <div className={b.daysLeft <= 1 ? 'text-rose-600 dark:text-rose-400 font-semibold' : 'text-on-surface-variant'}>
                        {b.daysLeft === 0 ? 'expires today' : `${b.daysLeft}d left`}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2"><StatusChip status={b.status} /></td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {!b.ewbNo && (
                      <button onClick={() => generate.mutate(b._id)} disabled={generate.isPending}
                        className="px-2.5 py-1 rounded-lg bg-primary text-on-primary font-semibold disabled:opacity-40">
                        Generate
                      </button>
                    )}
                    {b.status === 'active' && (
                      <button onClick={() => cancel.mutate(b._id)} disabled={cancel.isPending}
                        title="Only possible within 24 hours of generation"
                        className="ml-1 px-2.5 py-1 rounded-lg border border-outline font-semibold disabled:opacity-40">
                        <Ban className="w-3 h-3 inline" /> Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DraftFromInvoice({ onDone }: { onDone: () => void }) {
  const [invoiceId, setInvoiceId] = useState('');
  const [draft, setDraft] = useState<any>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [required, setRequired] = useState<boolean | null>(null);

  const load = useMutation({
    mutationFn: async () => (await api.post(`/api/ewaybill/draft-from/invoice/${invoiceId}`)).data,
    onSuccess: (d: any) => {
      setDraft(d.draft); setIssues(d.issues || []); setWarnings(d.warnings || []); setRequired(d.required);
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not load that invoice.'),
  });

  const save = useMutation({
    mutationFn: async () => (await api.post('/api/ewaybill', draft)).data,
    onSuccess: () => { toast.success('Draft saved.'); onDone(); },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not save.'),
  });

  const set = (k: string, v: any) => setDraft({ ...draft, [k]: v });

  return (
    <div className="rounded-xl border border-outline bg-surface p-4 space-y-3">
      <h3 className="text-sm font-bold">New e-way bill from an invoice</h3>
      <div className="flex flex-wrap gap-2">
        <input value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}
          placeholder="Sales invoice id"
          className="flex-1 min-w-[16rem] px-3 py-2 rounded-lg border border-outline bg-transparent text-sm font-mono" />
        <button onClick={() => load.mutate()} disabled={!invoiceId.trim() || load.isPending}
          className="px-3 py-2 rounded-lg border border-outline text-sm font-semibold disabled:opacity-40">
          Load
        </button>
      </div>

      {draft && (
        <>
          {/* Whether one is legally needed at all — so bills are not raised for
              every small consignment. */}
          {required === false && (
            <p className="text-xs text-on-surface-variant">
              This consignment is at or below ₹50,000, so an e-way bill is not required.
              You can still raise one.
            </p>
          )}

          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Distance (km)" value={draft.distanceKm ?? ''} onChange={(v) => set('distanceKm', Number(v) || 0)} />
            <Field label="Vehicle number" value={draft.vehicleNo ?? ''} onChange={(v) => set('vehicleNo', v.toUpperCase())} />
            <label className="block">
              <span className="text-xs font-semibold block mb-1">Vehicle type</span>
              <select value={draft.vehicleType || 'R'} onChange={(e) => set('vehicleType', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm">
                <option value="R">Regular</option>
                <option value="O">Over-dimensional cargo</option>
              </select>
            </label>
            <Field label="Transporter name" value={draft.transporterName ?? ''} onChange={(v) => set('transporterName', v)} />
            <Field label="Transporter ID" value={draft.transporterId ?? ''} onChange={(v) => set('transporterId', v)} />
          </div>

          <p className="text-[11px] text-on-surface-variant">
            Distance sets the validity — one day per 200 km (20 km for over-dimensional cargo),
            expiring at midnight.
          </p>

          {issues.length > 0 && (
            <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-xs text-rose-800 dark:text-rose-300">
              <p className="font-semibold flex items-center gap-1.5 mb-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Fix before generating
              </p>
              <ul className="list-disc ml-5 space-y-0.5">{issues.map((i) => <li key={i}>{i}</li>)}</ul>
            </div>
          )}
          {warnings.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-xs text-amber-900 dark:text-amber-200">
              <ul className="list-disc ml-5 space-y-0.5">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button onClick={onDone} className="px-3 py-1.5 rounded-lg border border-outline text-xs font-semibold">Cancel</button>
            <button onClick={() => save.mutate()} disabled={save.isPending}
              className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-semibold disabled:opacity-40">
              Save draft
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: any; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold block mb-1">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm" />
    </label>
  );
}

function StatusChip({ status }: { status: string }) {
  const tone = status === 'active' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
    : status === 'cancelled' ? 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
    : status === 'failed' || status === 'rejected' ? 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
    : status === 'expired' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${tone}`}>{status}</span>;
}

export default EwayBillPage;
